<template>
  <div class="fish-tabs" :class="[`fish-tabs--${estilo}`]">
    <div
      class="fish-tabs__trilha"
      role="tablist"
      :aria-label="rotulo"
      @keydown.left.prevent="andar(-1)"
      @keydown.right.prevent="andar(1)"
      @keydown.home.prevent="ir(0)"
      @keydown.end.prevent="ir(abas.length - 1)"
    >
      <button
        v-for="(aba, i) in abas"
        :key="aba.id"
        :ref="`aba-${i}`"
        class="fish-tabs__aba"
        :class="{ 'fish-tabs__aba--ativa': aba.id === modelValue }"
        type="button"
        role="tab"
        :aria-selected="aba.id === modelValue ? 'true' : 'false'"
        :aria-controls="`painel-${aba.id}`"
        :id="`aba-${aba.id}`"
        :tabindex="aba.id === modelValue ? 0 : -1"
        @click="ir(i)"
      >
        <FishIcon v-if="aba.icone" :nome="aba.icone" tamanho="pequeno" />
        <span v-if="!soIcone || aba.id === modelValue" class="fish-tabs__rotulo">{{ aba.rotulo }}</span>
        <span v-if="soIcone && aba.id !== modelValue" class="so-leitor">{{ aba.rotulo }}</span>

        <!-- O brilho suave anuncia novidade. Nunca um badge vermelho (D0). -->
        <span v-if="aba.novidade" class="fish-tabs__novidade repouso-brilha" aria-hidden="true" />
      </button>
    </div>

    <div
      class="fish-tabs__painel"
      role="tabpanel"
      :id="`painel-${modelValue}`"
      :aria-labelledby="`aba-${modelValue}`"
      tabindex="0"
    >
      <slot :ativa="modelValue" />
    </div>
  </div>
</template>

<script>
import FishIcon from './FishIcon.vue';

/**
 * 2.9 — `FishTabs`.
 *
 * ## A navegação por teclado não é opcional aqui
 *
 * O padrão de abas tem um comportamento esperado que quase nenhuma implementação caseira faz: **as
 * setas andam entre as abas, e só a aba ativa está na ordem do Tab** (`tabindex="-1"` nas outras).
 * Sem isso, um teclado precisa tabular por todas as abas para chegar ao conteúdo — e o Inventário
 * do Fishbay tem cinco.
 *
 * Com o cliente tendo seis declarações `:focus` no total (UI-29), este é o tipo de coisa que nunca
 * ia aparecer sozinha.
 *
 * ## O `soIcone`, e o cuidado que ele exige
 *
 * A loja usa abas em ícone (D16, 5.3) e a barra de navegação tira os rótulos (D5). Mas tirar o
 * rótulo **da tela** não é tirá-lo do documento: o `so-leitor` mantém o nome para quem não vê o
 * ícone. Um `<button>` com só uma imagem de fundo é um botão anônimo.
 *
 * O rótulo **reaparece na aba ativa**, que é a compensação que o D5 pede por tirar a migalha.
 */
export default {
  name: 'FishTabs',
  components: { FishIcon },
  props: {
    /** `[{ id, rotulo, icone?, novidade? }]` */
    abas: { type: Array, required: true },
    modelValue: { type: [String, Number], required: true },
    rotulo: { type: String, default: 'Seções' },
    soIcone: { type: Boolean, default: false },
    estilo: {
      type: String,
      default: 'aba',
      validator: (v) => ['aba', 'fita', 'pilula'].includes(v),
    },
  },
  emits: ['update:modelValue'],
  methods: {
    ir(i) {
      const aba = this.abas[i];
      if (!aba) return;
      this.$emit('update:modelValue', aba.id);
      // O foco acompanha a seleção: é o que o padrão de abas manda, e é o que faz a seta seguinte
      // partir de onde o usuário está, e não do começo.
      this.$nextTick(() => {
        const alvo = this.$refs[`aba-${i}`];
        (Array.isArray(alvo) ? alvo[0] : alvo)?.focus?.();
      });
    },
    andar(passo) {
      const atual = this.abas.findIndex((a) => a.id === this.modelValue);
      // Circular: da última a seta direita volta à primeira. É o esperado, e evita o beco sem saída
      // em que a seta simplesmente não faz nada.
      this.ir((atual + passo + this.abas.length) % this.abas.length);
    },
  },
};
</script>

<style scoped>
.fish-tabs__trilha {
  display: flex;
  gap: var(--e-1);
  overflow-x: auto;
  /* A trilha rola de lado quando não cabe, mas a barra de rolagem some: cinco abas num telefone de
   * 360px passam do limite, e uma barra visível ali é ruído. */
  scrollbar-width: none;
}

.fish-tabs__trilha::-webkit-scrollbar {
  display: none;
}

.fish-tabs__aba {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--e-2);
  flex-shrink: 0;

  font-family: var(--fonte-display);
  font-size: var(--f-2);
  /* Pergaminho apagado sobre madeira: a aba em repouso é lida, mas recuada. */
  color: var(--papel-sombra);
  min-height: var(--alvo-minimo);
  padding: var(--e-2) var(--e-3);

  border-style: solid;
  border-color: transparent;
  border-width: var(--aba-borda);
  border-image-source: url('/ui/aba.png');
  border-image-slice: var(--aba-fatia);
  border-image-width: var(--aba-borda);
  border-image-repeat: repeat;
  background: var(--madeira-sombra);
  background-clip: padding-box;
  image-rendering: pixelated;

  transition: color var(--t-rapido) var(--curva-suave),
    background-color var(--t-rapido) var(--curva-suave);
}

/* A aba ativa vira PERGAMINHO — o mesmo material da página que ela abre. É a convenção de aba que
 * todo mundo lê sem pensar, dita em material em vez de em cor. */
.fish-tabs__aba--ativa {
  border-image-source: url('/ui/aba-ativa.png');
  background: var(--papel);
  color: var(--tinta);
}

@media (hover: hover) {
  .fish-tabs__aba:not(.fish-tabs__aba--ativa):hover {
    color: var(--papel-luz);
  }
}

/* A fita: o marcador do caderno (D9). Vertical, na borda da página. */
.fish-tabs--fita .fish-tabs__trilha {
  flex-direction: column;
}

.fish-tabs--fita .fish-tabs__aba {
  border-image: none;
  border: 2px solid var(--madeira-escura);
  border-left-width: 6px;
  border-left-color: var(--cor-neutra);
  background: var(--papel-sombra);
  color: var(--tinta-fraca);
  justify-content: flex-start;
}

.fish-tabs--fita .fish-tabs__aba--ativa {
  background: var(--papel-frio);
  border-left-color: var(--ouro);
  color: var(--tinta);
}

.fish-tabs--pilula .fish-tabs__aba {
  border-image: none;
  border: 2px solid var(--papel-sombra);
  border-radius: var(--r-pilula);
  background: var(--papel);
  color: var(--tinta-fraca);
}

.fish-tabs--pilula .fish-tabs__aba--ativa {
  border-color: var(--ouro);
  background: var(--papel-luz);
  color: var(--tinta);
}

.fish-tabs__painel {
  padding-top: var(--e-3);
  outline: none;
}

/* A novidade: um ponto de latão que respira. Não é vermelho e não tem número — os dois são o que o
 * D0 chama de alarme e de urgência fabricada. */
.fish-tabs__novidade {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  border-radius: var(--r-circulo);
  background: var(--ouro-claro);
}
</style>
